import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { KeysOf, Namespace, useT } from "@racoon/i18n";
import { IcNavigation } from "@racoon/icons";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import { InfoRow } from "~/ui/components/infoRow";
import { JobPhotoReport } from "~/ui/components/jobPhotoReport";
import { ViewSelector } from "../../../shared/components/viewSelector";
import { ConnectProto, WasteType } from "@racoon/api";
import { getEnumName, fromTimestamp, enumColors } from "@racoon/lib";
import { getJobPhotos } from "~/shared/utils";
import { Button, fmt, getTypeBadge } from "@racoon/ui";
import { isToday } from "date-fns";
import { useGetJobByIdQuery } from "~/shared/api/govDash/govDash.api";
import { jobGpsTrackingUrl } from "~/pages/gps-tracking/model/url-state";
import { useCanEditGovDash } from "~/shared/hooks/useCanEditGovDash";

export const WasteExportDetails = ({
  selectedRecordId,
}: {
  selectedRecordId: string | undefined;
}) => {
  const t = useT();
  const canEditGovDash = useCanEditGovDash();
  const [activeSection, setActiveSection] = useState<"general" | "job">("general");

  const { data: selectedJob } = useGetJobByIdQuery(
    {
      id: selectedRecordId ? BigInt(selectedRecordId) : undefined,
    },
    { skip: !selectedRecordId },
  );

  const { beforePhotos, afterPhotos, failureReasonPhotos } = useMemo(
    () => getJobPhotos(selectedJob),
    [selectedJob],
  );

  // The job's truck on the job's day, played from the moment its service started.
  const gpsTrackingLink = useMemo(
    () => (selectedJob ? jobGpsTrackingUrl(selectedJob) : undefined),
    [selectedJob],
  );

  const viewOptions = useMemo(
    () =>
      [
        { value: "general" as const, label: t(Namespace.Govdash, "general") },
        { value: "job" as const, label: t(Namespace.Govdash, "wasteExport.task") },
      ] as const,
    [t],
  );

  const jurRows = useMemo(
    () => [
      {
        label: t(Namespace.Govdash, "pickupLocationId"),
        value: selectedJob?.pickupLocation?.id != null ? `PL${selectedJob.pickupLocation.id}` : "-",
      },
      {
        label: t(Namespace.Govdash, "pickupLocationType"),
        value: (() => {
          const typeName = getEnumName(
            ConnectProto.PickupLocationTypeSchema,
            selectedJob?.pickupLocation?.type,
          );
          return typeName ? t(Namespace.Enum, typeName) : "-";
        })(),
      },
      {
        label: t(Namespace.Govdash, "jurAddress"),
        value: selectedJob?.pickupLocation?.address || "-",
      },
      {
        label: t(Namespace.Govdash, "geolocation"),
        value:
          selectedJob?.pickupLocation?.lat != null && selectedJob?.pickupLocation?.lon != null ? (
            <a
              className="block max-w-xs text-brand-blue truncate"
              href={`https://2gis.kz/geo/${selectedJob.pickupLocation.lon},${selectedJob.pickupLocation.lat}`}
              target="_blank"
              rel="noreferrer"
            >
              2GIS
            </a>
          ) : (
            "-"
          ),
      },
      // TODO: implement once back-end provides
      // { label: t(Namespace.Govdash, "district"), value: "Bostandyk district" },
      {
        label: t(Namespace.Govdash, "servicingMvo"),
        value: selectedJob?.serviceProvider?.name || "-",
      },
      {
        label: t(Namespace.Govdash, "lastVisit"),
        value: (() => {
          const lastVisit = fromTimestamp(selectedJob?.updatedAt);
          if (!lastVisit) return "-";
          return isToday(lastVisit)
            ? `${t(Namespace.Govdash, "status.today")} ${fmt.time(lastVisit)}`
            : fmt.dateTime(lastVisit);
        })(),
      },
    ],
    [t, selectedJob],
  );

  // Nested proto messages are all optional, so narrow once here rather than at every access.
  const containerGroup = selectedJob?.containerGroup;
  const containerTypeName = containerGroup?.containerType?.name;
  const nominalVolume =
    containerGroup?.containerType?.volume != null && containerGroup?.numberOfContainers != null
      ? containerGroup.containerType.volume * containerGroup.numberOfContainers
      : undefined;

  const containerGroupRows = useMemo(
    () => [
      {
        label: t(Namespace.Govdash, "containerGroup"),
        value: containerTypeName
          ? `${containerTypeName} (${containerGroup?.numberOfContainers ?? 0})`
          : "-",
      },
      {
        label: t(Namespace.Govdash, "wasteType"),
        value: (() => {
          // getEnumName yields the protobuf JSON name ("WASTE_TYPE_MSW"), which is exactly
          // what getTypeBadge switches on — but its WasteType is the hand-written string enum
          // in @racoon/api's proto/, not the numeric one in gen/. Same values, distinct types.
          const enumName = getEnumName(ConnectProto.WasteTypeSchema, selectedJob?.type);
          if (!enumName) return "-";
          return getTypeBadge(enumName as unknown as WasteType, t);
        })(),
      },
      {
        label: t(Namespace.Govdash, "nominalVolume"),
        value:
          nominalVolume != null
            ? t(Namespace.Govdash, "status.volumeMetric", { volume: nominalVolume.toFixed(2) })
            : "-",
      },
      // {
      //   label: t(Namespace.Govdash, "collectionSchedule"),
      //   value: selectedJob?.pickupLocation?.gisLink ? (
      //     <a
      //       className="block max-w-xs min-w-20 truncate"
      //       href={selectedJob?.pickupLocation?.gisLink}
      //       target="_blank"
      //       rel="noreferrer"
      //     >
      //       {selectedJob?.pickupLocation?.gisLink}
      //     </a>
      //   ) : (
      //     "-"
      //   ),
      // },
      // TODO: implement once back-end provides
      // { label: t(Namespace.Govdash, "district"), value: "Bostandyk district" },
      {
        label: t(Namespace.Govdash, "wasteGenerator"),
        value: containerGroup?.ownerOrganization?.name || "-",
      },
    ],
    [t, selectedJob, containerGroup, containerTypeName, nominalVolume],
  );

  const jobRows = useMemo(
    () => [
      {
        label: t(Namespace.Govdash, "containerGroup"),
        value: containerTypeName
          ? `${containerTypeName} (${containerGroup?.numberOfContainers ?? 0})`
          : "-",
      },
      {
        label: t(Namespace.Govdash, "wasteExport.taskType"),
        value: (() => {
          const typeName = getEnumName(ConnectProto.JobTypeSchema, selectedJob?.type);
          return typeName ? t(Namespace.Enum, typeName) : "-";
        })(),
      },
      {
        label: t(Namespace.Govdash, "nominalVolume"),
        value:
          nominalVolume != null
            ? `${nominalVolume.toFixed(2)} ${t(Namespace.Govdash, "volumeMetric")}`
            : "-",
      },
      // {
      //   label: t(Namespace.Govdash, "collectionSchedule"),
      //   value: selectedJob?.pickupLocation?.gisLink ? (
      //     <a
      //       className="block max-w-xs min-w-20 truncate"
      //       href={selectedJob?.pickupLocation?.gisLink}
      //       target="_blank"
      //       rel="noreferrer"
      //     >
      //       {selectedJob?.pickupLocation?.gisLink}
      //     </a>
      //   ) : (
      //     "-"
      //   ),
      // },
      {
        label: t(Namespace.Govdash, "wasteGenerator"),
        value: containerGroup?.ownerOrganization?.name || "-",
      },
      // {
      //   label: t(Namespace.Govdash, "contract"),
      //   value: selectedJob?.driverOnShift?.truck?.plateNumber || "-",
      // },
      {
        label: t(Namespace.Govdash, "wasteExport.vehicleOnTask"),
        value: selectedJob?.driverOnShift?.truck?.plateNumber || "-",
      },
      {
        label: t(Namespace.Govdash, "wasteExport.exportStatus"),
        value: (() => {
          const jobInstant = fromTimestamp(selectedJob?.factEndAt ?? selectedJob?.updatedAt);
          const statusKey =
            `JOB_STATUS_${ConnectProto.JobStatus[selectedJob?.status ?? 0]}` as KeysOf<Namespace.Enum>;
          const statusColor = enumColors[statusKey];

          return (
            <div className="flex items-center gap-2">
              <span>{jobInstant ? fmt.dateTime(jobInstant, "dd MMM yyyy HH:mm") : "-"}</span>
              <div
                className={`min-w-[110px] py-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs justify-center ${statusColor?.bg ?? ""} ${statusColor?.color ?? ""} ${statusColor?.borderColor ? `${statusColor.borderColor} border` : ""}`}
              >
                {t(Namespace.Enum, statusKey)}
              </div>
            </div>
          );
        })(),
      },
      ...(selectedJob?.status === ConnectProto.JobStatus.NOT_COMPLETED
        ? [
            {
              label: t(Namespace.Govdash, "wasteExport.reason"),
              value: (
                <span className="text-brand-red truncate">
                  {selectedJob?.driverComment?.split("=")?.[1]?.split(":")?.[0] ??
                    t(Namespace.Govdash, "wasteExport.reasonNotSpecified")}
                </span>
              ),
            },
          ]
        : []),
    ],
    [t, selectedJob, containerGroup, containerTypeName, nominalVolume],
  );

  return (
    <CardContentWrapper hasDivider={false} className="flex min-h-0 flex-col overflow-hidden">
      <div className="p-2">
        <ViewSelector
          options={viewOptions}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>
      <div className="overflow-y-auto min-h-0 flex-1">
        {activeSection === "general" && (
          <>
            <div className="p-4 space-y-4">
              <p className="text-base font-semibold tracking-tight text-slate-900">
                {t(Namespace.Govdash, "subtitle.pickupLocation")}
              </p>
              <CardContentWrapper>
                {jurRows.map((row) => (
                  <InfoRow key={row.label} label={row.label} value={row.value} />
                ))}
              </CardContentWrapper>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-base font-semibold tracking-tight text-slate-900">
                {t(Namespace.Govdash, "containerGroups")}
              </p>
              <CardContentWrapper>
                {containerGroupRows.map((row) => (
                  <InfoRow key={row.label} label={row.label} value={row.value} />
                ))}
              </CardContentWrapper>
            </div>
          </>
        )}
        {activeSection === "job" && (
          <div>
            {jobRows.length > 0 && (
              <div className="p-4 space-y-4">
                <p className="text-base font-semibold tracking-tight text-slate-900">
                  {t(Namespace.Govdash, "wasteExport.taskDetails")}
                </p>
                <div className="space-y-3">
                  <CardContentWrapper>
                    {jobRows.map((row, i) => (
                      <InfoRow
                        key={`${row.label}-${i}`}
                        label={
                          row.label === t(Namespace.Govdash, "containerGroup")
                            ? `${t(Namespace.Govdash, "containerGroup")} ${i + 1}`
                            : row.label
                        }
                        value={row.value}
                      />
                    ))}
                  </CardContentWrapper>
                  {/* The screen is desktop only, for editors. */}
                  {gpsTrackingLink && canEditGovDash && (
                    <div className="hidden lg:block">
                      <Button asChild variant="opacityBlueWithOutline" className="w-full gap-2">
                        <Link to={gpsTrackingLink} data-testid="waste-export-gps-tracking">
                          <IcNavigation className="h-5 w-5" />
                          {t(Namespace.Govdash, "gpsTracking.openTrack")}
                        </Link>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="p-4 space-y-4">
              <p className="text-base font-semibold tracking-tight text-slate-900">
                {t(Namespace.Govdash, "wasteExport.photoReport")}
              </p>
              {beforePhotos.length === 0 &&
              afterPhotos.length === 0 &&
              failureReasonPhotos.length === 0 ? (
                <p className="text-sm text-text-secondary">
                  {t(Namespace.Govdash, "wasteExport.noPhotos")}
                </p>
              ) : (
                <JobPhotoReport
                  beforePhotos={beforePhotos}
                  afterPhotos={afterPhotos}
                  failureReasonPhotos={failureReasonPhotos}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </CardContentWrapper>
  );
};
